import { Action as ReduxAction, handleActions, ReducerMap } from "redux-actions";
import { IImportReport } from "../../../../api/CourseAuthorApi";
import { IOrganizationDisplayInfo, IOrganizationTree } from "../../../../api/ResolveApi/resolve-api.types";
import { IUser } from "../../../Analytics/Common";
import { ICompleteOrganization, RequestState } from "../../../types/common";
import { Action } from "../actions";
import { IErrorMessage, IFetchInitialData, IMiddleError, IProgressSteps, ReportRequested } from "../types";

export interface IOrganizationSettingsState {
  fetchInitialDataState: RequestState;
  fetchInitialError: string | null;
  importReport: IImportReport | null;
  organization: ICompleteOrganization | null;
  organizationAdmins: IUser[];
  patchRequestImportError: IErrorMessage | null;
  patchRequestImportState: RequestState;
  patchRequestState: RequestState;
  patchRequestError: string | null;
  refreshSettingsState: RequestState;
  refreshSettingsError: string | null;
  requestAllTableDataStatus: RequestState;
  requestedReport: ReportRequested | null;
  middleErrors: IMiddleError[];
  stepData: IProgressSteps;
  preview: IOrganizationTree | null;
}

const INIT_STEP = {
  stepNumber: 0,
  totalSteps: 0,
};

export const INITIAL_STATE: IOrganizationSettingsState = {
  fetchInitialDataState: RequestState.IDLE,
  fetchInitialError: null,
  importReport: null,
  middleErrors: [],
  organization: null,
  organizationAdmins: [],
  patchRequestError: null,
  patchRequestImportError: null,
  patchRequestImportState: RequestState.IDLE,
  patchRequestState: RequestState.IDLE,
  preview: null,
  refreshSettingsError: null,
  refreshSettingsState: RequestState.IDLE,
  requestAllTableDataStatus: RequestState.IDLE,
  requestedReport: null,
  stepData: INIT_STEP,
};

const mount = (_: IOrganizationSettingsState): IOrganizationSettingsState => INITIAL_STATE;

const unmount = (_: IOrganizationSettingsState): IOrganizationSettingsState => INITIAL_STATE;

const fetchInitialData = (state: IOrganizationSettingsState): IOrganizationSettingsState => ({
  ...state,
  fetchInitialDataState: RequestState.PENDING,
});

const fetchInitialDataFailure = (state: IOrganizationSettingsState): IOrganizationSettingsState => ({
  ...state,
  fetchInitialDataState: RequestState.FAILED,
});

const fetchInitialDataSuccess = (
  state: IOrganizationSettingsState,
  action: ReduxAction<IFetchInitialData>
): IOrganizationSettingsState => {
  return {
    ...state,
    fetchInitialDataState: RequestState.SUCCEEDED,
    organization: action.payload.organization,
    organizationAdmins: action.payload.admins,
  };
};

const patchRequest = (state: IOrganizationSettingsState): IOrganizationSettingsState => ({
  ...state,
  patchRequestState: RequestState.PENDING,
});

const patchRequestFailure = (state: IOrganizationSettingsState): IOrganizationSettingsState => ({
  ...state,
  patchRequestState: RequestState.FAILED,
});

const patchRequestSuccess = (
  state: IOrganizationSettingsState,
  action: ReduxAction<ICompleteOrganization>
): IOrganizationSettingsState => ({
  ...state,
  organization: action.payload,
  patchRequestState: RequestState.SUCCEEDED,
});

const patchRequestImport = (state: IOrganizationSettingsState): IOrganizationSettingsState => ({
  ...state,
  patchRequestImportState: RequestState.PENDING,
});

const patchRequestImportFailure = (
  state: IOrganizationSettingsState,
  action: ReduxAction<IErrorMessage>
): IOrganizationSettingsState => ({
  ...state,
  patchRequestImportError: action.payload,
  patchRequestImportState: RequestState.FAILED,
});

const patchRequestImportSuccess = (
  state: IOrganizationSettingsState,
  action: ReduxAction<IImportReport>
): IOrganizationSettingsState => ({
  ...state,
  importReport: action.payload,
  patchRequestImportError: null,
  patchRequestImportState: RequestState.SUCCEEDED,
});

const refreshSettings = (state: IOrganizationSettingsState): IOrganizationSettingsState => ({
  ...state,
  refreshSettingsState: RequestState.PENDING,
});

const refreshSettingsFailure = (state: IOrganizationSettingsState): IOrganizationSettingsState => ({
  ...state,
  refreshSettingsState: RequestState.FAILED,
});

const refreshSettingsSuccess = (
  state: IOrganizationSettingsState,
  action: ReduxAction<IFetchInitialData>
): IOrganizationSettingsState => ({
  ...state,
  organization: action.payload.organization,
  organizationAdmins: action.payload.admins,
  refreshSettingsState: RequestState.SUCCEEDED,
});

const requestAllUsersData = (state: IOrganizationSettingsState): IOrganizationSettingsState => ({
  ...state,
  middleErrors: [],
  requestAllTableDataStatus: RequestState.PENDING,
  requestedReport: "users",
  stepData: INIT_STEP,
});

const requestAllAssessmentData = (state: IOrganizationSettingsState): IOrganizationSettingsState => ({
  ...state,
  middleErrors: [],
  requestAllTableDataStatus: RequestState.PENDING,
  requestedReport: "assessments",
  stepData: INIT_STEP,
});

const requestAllTableDataFinished = (state: IOrganizationSettingsState): IOrganizationSettingsState => ({
  ...state,
  requestAllTableDataStatus: RequestState.SUCCEEDED,
});

const requestAllTableDataFatalError = (state: IOrganizationSettingsState): IOrganizationSettingsState => ({
  ...state,
  requestAllTableDataStatus: RequestState.FAILED,
});

const middleErrorReport = (
  state: IOrganizationSettingsState,
  action: ReduxAction<IMiddleError>
): IOrganizationSettingsState => ({
  ...state,
  middleErrors: state.middleErrors.concat([action.payload]),
});

const stepOnAllTableDataRequest = (
  state: IOrganizationSettingsState,
  action: ReduxAction<IProgressSteps>
): IOrganizationSettingsState => ({
  ...state,
  stepData: action.payload,
});

const organizationPreviewSuccess = (
  state: IOrganizationSettingsState,
  action: ReduxAction<IOrganizationTree>
): IOrganizationSettingsState => ({
  ...state,
  preview: action.payload,
});

const handleCreateNewOrganizationSuccess = (
  state: IOrganizationSettingsState,
  action: ReduxAction<IOrganizationTree>
): IOrganizationSettingsState => ({
  ...state,
  preview: action.payload,
});

const handleChangeOrganizationName = (
  state: IOrganizationSettingsState,
  action: ReduxAction<IOrganizationTree>
): IOrganizationSettingsState => ({
  ...state,
  preview: action.payload,
});

const handleDeleteOrganizationFromPreview = (
  state: IOrganizationSettingsState,
  action: ReduxAction<IOrganizationTree>
): IOrganizationSettingsState => ({
  ...state,
  preview: action.payload,
});

type Payload =
  | IFetchInitialData
  | ICompleteOrganization
  | IImportReport
  | IProgressSteps
  | IOrganizationDisplayInfo
  | { message: string }
  | string
  | void;

const reduxactionsReducers: ReducerMap<IOrganizationSettingsState, Payload> = {
  [Action.MOUNT]: mount,
  [Action.FETCH_INITIAL_DATA]: fetchInitialData,
  [Action.HANDLE_FETCH_INITIAL_DATA_FAILURE]: fetchInitialDataFailure,
  [Action.HANDLE_FETCH_INITIAL_DATA_SUCCESS]: fetchInitialDataSuccess,
  [Action.PATCH_REQUEST]: patchRequest,
  [Action.PATCH_REQUEST_FAILURE]: patchRequestFailure,
  [Action.PATCH_REQUEST_SUCCESS]: patchRequestSuccess,
  [Action.PATCH_REQUEST_IMPORT]: patchRequestImport,
  [Action.PATCH_REQUEST_IMPORT_FAILURE]: patchRequestImportFailure,
  [Action.PATCH_REQUEST_IMPORT_SUCCESS]: patchRequestImportSuccess,
  [Action.REFRESH_SETTINGS]: refreshSettings,
  [Action.REFRESH_SETTINGS_FAILURE]: refreshSettingsFailure,
  [Action.REFRESH_SETTINGS_SUCCESS]: refreshSettingsSuccess,
  [Action.REQUEST_ALL_USER_DATA]: requestAllUsersData,
  [Action.REQUEST_ALL_ASSESSMENT_DATA]: requestAllAssessmentData,
  [Action.REQUEST_ALL_TABLE_DATA_FINISHED]: requestAllTableDataFinished,
  [Action.REQUEST_ALL_TABLE_DATA_FATAL_ERROR]: requestAllTableDataFatalError,
  [Action.REQUEST_ALL_TABLE_DATA_MIDDLE_ERROR]: middleErrorReport,
  [Action.REQUEST_ALL_TABLE_DATA_FINISHED]: requestAllTableDataFinished,
  [Action.STEP_ON_ALL_TABLE_DATA]: stepOnAllTableDataRequest,
  [Action.HANDLE_ORGANIZATION_PREVIEW_SUCCESS]: organizationPreviewSuccess,
  [Action.HANDLE_CREATE_NEW_ORGANIZATION_SUCCESS]: handleCreateNewOrganizationSuccess,
  [Action.HANDLE_GET_ORG_INFO_SUCCESS]: handleChangeOrganizationName,
  [Action.HANDLE_DELETE_ORGANIZATION_FROM_PREVIEW_SUCCESS]: handleDeleteOrganizationFromPreview,
  [Action.UNMOUNT]: unmount,
};

export default handleActions<IOrganizationSettingsState, Payload>(reduxactionsReducers, INITIAL_STATE);
